import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get token from localStorage
const getToken = () => localStorage.getItem('tactical_token');

// Axios interceptor — attach Authorization header to every request automatically.
// The JWT is NEVER put in a query string: that leaks a 24h credential into nginx
// access logs and browser history on every call. The only exceptions are the two
// helpers that build URLs for direct browser navigation (file download / PDF tab),
// where the browser cannot send a header.
api.interceptors.request.use((config) => {
  const token = getToken();
  // Skip the admin-login endpoint (uses username/password instead of JWT)
  const url = (config.url || '').toLowerCase();
  if (token && !url.startsWith('/admin/login')) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Auth API
export const authApi = {
  getLoginUrl: async () => {
    const response = await api.get('/auth/login');
    return response.data;
  },
  
  getMe: async () => {
    const token = getToken();
    if (!token) throw new Error('Not authenticated');
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  logout: async () => {
    await api.post('/auth/logout');
    localStorage.removeItem('tactical_token');
  },

  // Redeem the single-use code from the Entra callback for the session JWT.
  codeExchange: async (code) => {
    const response = await api.post('/auth/code-exchange', { code });
    return response.data;
  },

  // Token exchange for iframe authentication
  tokenExchange: async (msToken, email) => {
    const response = await api.post('/auth/token-exchange', {
      token: msToken,
      email: email,
    });
    return response.data;
  },

  // Dev bypass — preview environments only
  getDevStatus: async () => {
    const response = await api.get('/auth/dev-status');
    return response.data;
  },

  listDevUsers: async () => {
    const response = await api.get('/auth/dev-users');
    return response.data;
  },

  devLogin: async (email) => {
    const response = await api.post('/auth/dev-login', { email });
    return response.data;
  },
};

// Tactical API
export const tacticalApi = {
  getCurrent: async () => {
    const response = await api.get(`/tacticals/current`);
    return response.data;
  },
  
  create: async (data = {}) => {
    const response = await api.post(`/tacticals/create`, data);
    return response.data;
  },
  
  update: async (tacticalId, data) => {
    const response = await api.put(`/tacticals/${tacticalId}`, data);
    return response.data;
  },
  
  addFacilityItem: async (tacticalId, data) => {
    const response = await api.post(`/tacticals/${tacticalId}/add-item`, data);
    return response.data;
  },
  
  getArchives: async () => {
    const response = await api.get(`/tacticals/archives`);
    return response.data;
  },
  
  rollover: async (siteIdsToKeep = null) => {
    let url = `/tacticals/rollover`;
    if (siteIdsToKeep && siteIdsToKeep.length > 0) {
      const siteParams = siteIdsToKeep.map(id => `site_ids_to_keep=${id}`).join('&');
      url += `&${siteParams}`;
    }
    const response = await api.post(url);
    return response.data;
  },
  
  // Facility Site Management
  addSite: async (tacticalId, name) => {
    const response = await api.post(`/tacticals/${tacticalId}/sites`, { name });
    return response.data;
  },
  
  updateSite: async (tacticalId, siteId, data) => {
    const response = await api.put(`/tacticals/${tacticalId}/sites/${siteId}`, data);
    return response.data;
  },
  
  deleteSite: async (tacticalId, siteId) => {
    const response = await api.delete(`/tacticals/${tacticalId}/sites/${siteId}`);
    return response.data;
  },
  
  reorderSites: async (tacticalId, siteIds) => {
    const response = await api.put(`/tacticals/${tacticalId}/sites/reorder`, { site_ids: siteIds });
    return response.data;
  },
  
  // TAH (Therapy @Home) Management
  enableTah: async (tacticalId) => {
    const response = await api.post(`/tacticals/${tacticalId}/tah/enable`);
    return response.data;
  },
  
  disableTah: async (tacticalId) => {
    const response = await api.delete(`/tacticals/${tacticalId}/tah/disable`);
    return response.data;
  },
  
  addAttachment: async (tacticalId, itemId, file) => {
    
    // Convert file to base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
    });
    
    const response = await api.post(
      `/tacticals/${tacticalId}/facility/${itemId}/attachment`,
      {
        filename: file.name,
        content_type: file.type,
        data: base64,
      }
    );
    return response.data;
  },
  
  deleteAttachment: async (tacticalId, itemId, attachmentId) => {
    const response = await api.delete(
      `/tacticals/${tacticalId}/facility/${itemId}/attachment/${attachmentId}`
    );
    return response.data;
  },
  
  // Direct browser navigation (download / new tab) — the browser cannot set an
  // Authorization header on these, so the token stays in the query string here.
  getAttachmentUrl: (tacticalId, itemId, attachmentId) => {
    const token = getToken();
    return `${API_BASE}/tacticals/${tacticalId}/facility/${itemId}/attachment/${attachmentId}/download?authorization=Bearer ${token}`;
  },
  
  acknowledgeChanges: async (tacticalId) => {
    const response = await api.post(`/tacticals/${tacticalId}/acknowledge-changes`);
    return response.data;
  },
  
  managerDeleteItem: async (tacticalId, itemId) => {
    const response = await api.post(`/tacticals/${tacticalId}/facility/${itemId}/manager-delete`);
    return response.data;
  },
  
  // Announcements
  getUnreadAnnouncements: async () => {
    const response = await api.get(`/tacticals/announcements/unread`);
    return response.data;
  },
  
  acknowledgeAnnouncement: async (announcementId) => {
    const response = await api.post(`/tacticals/announcements/${announcementId}/acknowledge`);
    return response.data;
  },
  
  // Feedback/Suggestions
  submitFeedback: async (category, title, description) => {
    const response = await api.post(`/tacticals/feedback`, {
      category,
      title,
      description,
    });
    return response.data;
  },
  
  getMyFeedback: async () => {
    const response = await api.get(`/tacticals/feedback/mine`);
    return response.data;
  },
};

// Manager API
export const managerApi = {
  invite: async (managerEmail) => {
    const response = await api.post(`/manager/invite`, {
      manager_email: managerEmail,
    });
    return response.data;
  },
  
  getRequests: async () => {
    const response = await api.get(`/manager/requests`);
    return response.data;
  },
  
  handleRequest: async (requestId, action) => {
    const response = await api.post(`/manager/requests/${requestId}/action`, {
      action,
    });
    return response.data;
  },
  
  getSubordinates: async () => {
    const response = await api.get(`/manager/subordinates`);
    return response.data;
  },
  
  getSubordinateTactical: async (userId) => {
    const response = await api.get(`/manager/subordinate/${userId}/tactical`);
    return response.data;
  },

  getSubordinateArchives: async (userId) => {
    const response = await api.get(`/manager/subordinate/${userId}/archives`);
    return response.data;
  },

  getSubordinateTacticalById: async (userId, tacticalId) => {
    const response = await api.get(`/manager/subordinate/${userId}/tactical/${tacticalId}`);
    return response.data;
  },

  getAllArchives: async () => {
    const response = await api.get(`/manager/all-archives`);
    return response.data;
  },
  
  getMyRequests: async () => {
    const response = await api.get(`/manager/my-requests`);
    return response.data;
  },
  
  getMyRequestsWithStatus: async () => {
    const response = await api.get(`/manager/my-requests-with-status`);
    return response.data;
  },
  
  removeSubordinate: async (userId) => {
    const response = await api.delete(`/manager/subordinate/${userId}`);
    return response.data;
  },
};

// User Search API
export const userApi = {
  search: async (query) => {
    const response = await api.get(`/users/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },
};

// Admin API
export const adminApi = {
  // Announcements
  getAnnouncements: async () => {
    const token = localStorage.getItem('admin_token');
    const response = await api.get(`/admin/announcements`);
    return response.data;
  },
  
  createAnnouncement: async (title, message) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.post(`/admin/announcements`, {
      title,
      message,
    });
    return response.data;
  },
  
  updateAnnouncement: async (announcementId, data) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.put(`/admin/announcements/${announcementId}`, data);
    return response.data;
  },
  
  deleteAnnouncement: async (announcementId) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.delete(`/admin/announcements/${announcementId}`);
    return response.data;
  },
  
  // Feedback Management
  getFeedback: async (status = null) => {
    const token = localStorage.getItem('admin_token');
    const url = status 
      ? `/admin/feedback?status=${status}`
      : `/admin/feedback`;
    const response = await api.get(url);
    return response.data;
  },
  
  getFeedbackStats: async () => {
    const token = localStorage.getItem('admin_token');
    const response = await api.get(`/admin/feedback/stats`);
    return response.data;
  },
  
  updateFeedbackStatus: async (feedbackId, status, adminNotes = null) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.put(`/admin/feedback/${feedbackId}/status`, {
      status,
      admin_notes: adminNotes,
    });
    return response.data;
  },
  
  deleteFeedback: async (feedbackId) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.delete(`/admin/feedback/${feedbackId}`);
    return response.data;
  },
  
  // TAH Template Management
  getTahTemplate: async () => {
    const token = localStorage.getItem('admin_token');
    const response = await api.get(`/admin/tah-template`);
    return response.data;
  },
  
  createTahTemplateItem: async (name) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.post(`/admin/tah-template`, { name });
    return response.data;
  },
  
  updateTahTemplateItem: async (itemId, data) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.put(`/admin/tah-template/${itemId}`, data);
    return response.data;
  },
  
  deleteTahTemplateItem: async (itemId) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.delete(`/admin/tah-template/${itemId}`);
    return response.data;
  },

  // User Management / Impersonation
  getUsers: async (search = '') => {
    const token = localStorage.getItem('admin_token');
    const url = search
      ? `/admin/users?q=${encodeURIComponent(search)}`
      : `/admin/users`;
    const response = await api.get(url);
    return response.data;
  },

  impersonateUser: async (userId) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.post(`/admin/impersonate/${userId}`);
    return response.data;
  },
};

// ==================== 5 STAR FEEDBACK API ====================

export const fiveStarApi = {
  // Categories
  listCategories: async (includeInactive = false) => {
    const response = await api.get(`/five-star/categories${includeInactive ? '&include_inactive=true' : ''}`);
    return response.data;
  },

  seedDefaultCategories: async () => {
    const token = localStorage.getItem('admin_token');
    const response = await api.post(`/five-star/categories/seed-defaults`);
    return response.data;
  },

  createCategory: async (data) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.post(`/five-star/categories`, data);
    return response.data;
  },

  updateCategory: async (id, data) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.put(`/five-star/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id) => {
    const token = localStorage.getItem('admin_token');
    const response = await api.delete(`/five-star/categories/${id}`);
    return response.data;
  },

  // Reviews
  createReview: async (data) => {
    const response = await api.post(`/five-star/reviews`, data);
    return response.data;
  },

  listMyReviews: async (includeArchived = true) => {
    const response = await api.get(`/five-star/reviews/mine?include_archived=${includeArchived}`);
    return response.data;
  },

  listManagedReviews: async (includeArchived = true) => {
    const response = await api.get(`/five-star/reviews/managed?include_archived=${includeArchived}`);
    return response.data;
  },

  getReview: async (id) => {
    const response = await api.get(`/five-star/reviews/${id}`);
    return response.data;
  },

  deleteReview: async (id) => {
    // Super-admin only on the server. UI button is gated client-side too.
    const response = await api.delete(`/five-star/reviews/${id}`);
    return response.data;
  },

  restoreReview: async (id) => {
    // Super-admin only. Restores from the snapshot stored on the delete activity-log entry.
    const response = await api.post(`/five-star/reviews/${id}/restore`);
    return response.data;
  },

  reassignAreaManager: async (id, areaManagerUserId) => {
    // Initiating manager OR admin; only allowed pre-synthesis.
    const response = await api.patch(
      `/five-star/reviews/${id}/area-manager`,
      { area_manager_user_id: areaManagerUserId },
    );
    return response.data;
  },

  saveAmPushback: async (id, categoryId, note) => {
    // Area Manager threaded push-back note per category. Only during the
    // area_manager_review step; only the assigned AM may call.
    const response = await api.post(
      `/five-star/reviews/${id}/am-pushback`,
      { category_id: categoryId, note },
    );
    return response.data;
  },

  deleteAmPushback: async (id, categoryId) => {
    const response = await api.delete(
      `/five-star/reviews/${id}/am-pushback/${categoryId}`,
    );
    return response.data;
  },

  bulkTransferAreaManager: async ({ fromUserId, toUserId, lookbackYears = 1, includeCompleted = false }) => {
    // Super-admin only. Reassigns all in-flight reviews from one AM to another within a lookback window.
    const response = await api.post(
      `/five-star/admin/transfer-area-manager`,
      {
        from_area_manager_user_id: fromUserId,
        to_area_manager_user_id: toUserId,
        lookback_years: lookbackYears,
        include_completed: includeCompleted,
      },
    );
    return response.data;
  },

  adminAnalytics: async () => {
    const response = await api.get(`/five-star/admin/analytics`);
    return response.data;
  },

  saveDraft: async (id, step, data) => {
    const response = await api.post(`/five-star/reviews/${id}/draft`, { step, data });
    return response.data;
  },

  submitManagerRating: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/manager-rate`, payload);
    return response.data;
  },

  submitAreaManagerReview: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/area-manager-review`, payload);
    return response.data;
  },

  editManagerRating: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/manager-rating/edit`, payload);
    return response.data;
  },

  editAreaManagerReview: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/area-manager-review/edit`, payload);
    return response.data;
  },

  // Personal archive — hide/show a completed review from the current user's list.
  archiveReview: async (id) => {
    const response = await api.post(`/five-star/reviews/${id}/archive`);
    return response.data;
  },
  unarchiveReview: async (id) => {
    const response = await api.post(`/five-star/reviews/${id}/unarchive`);
    return response.data;
  },

  // Super-admin surgical override — change manager / area-manager / status
  // (rewind only). Reason is required. Returns the updated review.
  adminOverride: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/admin-override`, payload);
    return response.data;
  },

  submitSynthesis: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/synthesize`, payload);
    return response.data;
  },

  submitSignOff: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/sign-off`, payload);
    return response.data;
  },

  submitDiscussion: async (id, payload) => {
    const response = await api.post(`/five-star/reviews/${id}/discuss`, payload);
    return response.data;
  },

  getTrend: async (employeeUserId) => {
    const response = await api.get(`/five-star/reviews/${employeeUserId}/trend`);
    return response.data;
  },

  getTeam: async () => {
    const response = await api.get(`/five-star/team`);
    return response.data;
  },

  // Phase 4 endpoints (review type + AM opt-out + eligible lists + PDF)
  listEligibleEmployees: async () => {
    const response = await api.get(`/five-star/eligible-employees`);
    return response.data;
  },

  listEligibleManagers: async () => {
    const response = await api.get(`/five-star/eligible-managers`);
    return response.data;
  },

  areaManagerOptOut: async (id, notes = '') => {
    const response = await api.post(`/five-star/reviews/${id}/area-manager-opt-out`, { notes });
    return response.data;
  },

  // Direct browser navigation (opens in a new tab) — keeps the query param for
  // the same reason as getAttachmentUrl above.
  exportPdfUrl: (id) => {
    const token = getToken();
    return `${api.defaults.baseURL}/five-star/reviews/${id}/export-pdf?authorization=Bearer ${token}`;
  },

  generateAiSummary: async (id) => {
    const response = await api.post(`/five-star/reviews/${id}/ai-summarize`);
    return response.data; // { summary: string, uses_remaining: number }
  },

  // Admin roles
  listMyRoles: async () => {
    const response = await api.get(`/admin-roles/me`);
    return response.data;
  },
  listAdminUsers: async () => {
    const response = await api.get(`/admin-roles/users`);
    return response.data;
  },
  searchUsersForRole: async (q) => {
    const response = await api.get(`/admin-roles/searchable-users?q=${encodeURIComponent(q || '')}`);
    return response.data;
  },
  grantRole: async (userId, role) => {
    const response = await api.post(`/admin-roles/grant`, { user_id: userId, role });
    return response.data;
  },
  revokeRole: async (userId, role) => {
    const response = await api.delete(`/admin-roles/${userId}/${role}`);
    return response.data;
  },
  getFacilityTemplate: async () => {
    const response = await api.get(`/admin-roles/templates/facility`);
    return response.data;
  },
  updateFacilityTemplate: async (items) => {
    const response = await api.put(`/admin-roles/templates/facility`, { items });
    return response.data;
  },

  // Review initiator management (Phase 4 — item #6)
  listReviewInitiators: async () => {
    const response = await api.get(`/admin-roles/review-initiators`);
    return response.data;
  },
  syncReviewInitiatorsFromEntra: async () => {
    const response = await api.post(`/admin-roles/review-initiators/sync-entra`);
    return response.data;
  },
  grantRole: async (userId, role) => {
    const response = await api.post(`/admin-roles/grant`, { user_id: userId, role });
    return response.data;
  },
  revokeRole: async (userId, role) => {
    const response = await api.delete(`/admin-roles/${userId}/${role}`);
    return response.data;
  },
};

export default api;
